import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { callAjaxIO } from 'Shared/ajax_io';
import type { FlaggedPageData } from './help_out_types';

// The server-rendered first page ages from page load, not from whenever
// the cache entry is created — without this, an entry reseeded from props
// would present a backlog someone has since worked off as fresh.
const pageLoadedAt = Date.now();

/**
 * One page of the flagged-page table. The server slices a set it already
 * holds in memcache, so a filter click is a round trip but not a query;
 * keepPreviousData leaves the rows that are up in place, dimmed, while the
 * next slice lands, which is what makes clicking through the flags feel
 * like moving rather than reloading.
 *
 * staleTime only has to cover the first paint, so the seeded page is not
 * refetched the moment it arrives. Coming back to the page re-asks: these
 * are shared queues and someone else has been working them.
 */
export function useFlaggedPages(flag: string, page: number, initial: FlaggedPageData) {
   const isInitial = flag === initial.flag && page === initial.page;
   return useQuery({
      queryKey: ['helpOutFlagged', flag, page],
      queryFn: () => callAjaxIO<FlaggedPageData>('getFlaggedPages', { flag, page }),
      initialData: isInitial ? initial : undefined,
      initialDataUpdatedAt: pageLoadedAt,
      placeholderData: keepPreviousData,
      staleTime: 30_000,
   });
}
